import { FC, useEffect, useState } from "react";
import { LoadingStatus, DocumentType } from "../../../../@types/enumsGlobal";
import { DefaultGrid } from "../../../../components/grids/default/defaultGrid";
import { PluginWrapper } from "../../../../components/plugins";
import useGridFilter, { CreateSelectorGridFilter } from "../../../../system/hooks/useGridFilter";
import { useAppContext } from "../../../../system/providers/appContextProvider";
import { usePluginContext } from "../../../../system/providers/plugin/PluginContextProvider";
import { IMarketingCompensatedDiscountsProductsViewDTO } from "../../../../libs/coreapi-dto/dirs/marketingCompensatedDiscounts";
import { MarketingCompensatedDiscountsProductsDataProvider } from "../../../../Services/DataProviders/MarketingCompensatedDiscountsDataProvider";

export const MarketingCompensatedDiscountsProductsGrid: FC<IGridProps> = (props) => {
    const appCtx = useAppContext();
    const pluginCtx = usePluginContext();

    const marketingCompensatedDiscountsProductsDP = new MarketingCompensatedDiscountsProductsDataProvider(appCtx.coreApiService);
    const [data, setData] = useState<IMarketingCompensatedDiscountsProductsViewDTO[]>([]);
    const [loadingStatus, setLoadingStatus] = useState<LoadingStatus>(LoadingStatus.NoData);
    const [totalCount, setTotalCount] = useState(0);
    const [gridFilter, dispatchGridFilter] = useGridFilter(CreateSelectorGridFilter);
    const [selectedItem, setSelectedItem] = useState<IGridRow>();
    const [selectedItems, setSelectedItems] = useState<IGridRow[]>(props.data ?? []);

    const loadData = () => {
        if (!pluginCtx.masterGrid.selectedItem?.idGlobal) {
            return;
        }

        const hasMarketingCompensatedDiscountFilter = gridFilter.columnFilters?.some(
            filter => filter.name === 'IdMarketingCompensatedDiscountGlobal'
        );

        if (!hasMarketingCompensatedDiscountFilter) {
            return;
        }

        setLoadingStatus(LoadingStatus.InProcess);

        try {
            marketingCompensatedDiscountsProductsDP.getViewParam(gridFilter, (data, totalCount) => {
                setData(data);
                setTotalCount(totalCount);
                data.length > 0 ? setLoadingStatus(LoadingStatus.Completed) : setLoadingStatus(LoadingStatus.NoData);
            });
        } catch (error) {
            setLoadingStatus(LoadingStatus.Failed);
            console.error(error);
        }
    };

    useEffect(() => {
        dispatchGridFilter({
            type: "deleteSpecificParamFilter",
            payload: 'IdContractorGlobalList'
        });

        const ids = props.marketingFilters?.pharmaciesFilter?.ids;
        if (!Array.isArray(ids) || ids.length === 0) return;

        if (gridFilter.paramFilter) {
            dispatchGridFilter({
                type: "addParamFilter",
                payload: {
                    IdContractorGlobalList: ids
                }
            });
        } else {
            dispatchGridFilter({
                type: "paramSimpleFilter",
                payload: {
                    gridColumnFilter: gridFilter.columnFilters,
                    gridParamFilter: {
                        IdContractorGlobalList: ids
                    }
                }
            });
            }
    }, [props.marketingFilters?.pharmaciesFilter])

    useEffect(() => {
        if (!pluginCtx.masterGrid.selectedItem?.idGlobal && props.marketingFilters?.documentType === DocumentType.marketingCompensatedDiscounts) {
            dispatchGridFilter({
                type: 'deleteColumnFilter',
                payload: "IdMarketingCompensatedDiscountGlobal"
            });
            return;
        }

        if (props.marketingFilters?.documentType === DocumentType.marketingCompensatedDiscounts) {
            dispatchGridFilter({
                type: 'addColumnFilter',
                payload: {
                    name: "IdMarketingCompensatedDiscountGlobal",
                    operator: "Eq",
                    value: pluginCtx.masterGrid.selectedItem?.idGlobal
                }
            });
        }
    }, [pluginCtx.masterGrid.selectedItem, props.marketingFilters?.documentType]);

    useEffect(() => {
        if (props.marketingFilters?.goodsNameFilter) {
            dispatchGridFilter({
                type: 'addColumnFilter',
                payload: {
                    name: "goodsName",
                    value: `%${props.marketingFilters.goodsNameFilter}%`,
                    operator: "Like",
                    invisible: false
                },
            });
        } else {
            dispatchGridFilter({ type: "deleteColumnFilter", payload: "goodsName" });
        }
    }, [props.marketingFilters?.goodsNameFilter]);

    useEffect(() => {
        if (props.marketingFilters?.contractorNameFilter) {
            dispatchGridFilter({
                type: 'addColumnFilter',
                payload: {
                    name: "legalEntityName",
                    value: `%${props.marketingFilters.contractorNameFilter}%`,
                    operator: "Like",
                    invisible: false
                },
            });
        } else {
            dispatchGridFilter({ type: "deleteColumnFilter", payload: "legalEntityName" });
        }
    }, [props.marketingFilters?.contractorNameFilter]);

    useEffect(() => {
        loadData();
    }, [gridFilter]);

    return (
        <PluginWrapper>
            <DefaultGrid
                showQuantity
                gridId={props.gridId}
                data={data}
                loadingStatus={loadingStatus}
                setLoadingStatus={setLoadingStatus}
                filter={gridFilter}
                getView={(gridFilter, callback) => {
                    marketingCompensatedDiscountsProductsDP.getViewParam(gridFilter, (data) => {
                        callback(data);
                    });
                }}
                totalCount={totalCount}
                dataProvider={marketingCompensatedDiscountsProductsDP}
                selectedItem={selectedItem}
                selectedItems={selectedItems}
                onSelect={(row) => {
                    setSelectedItem(row)
                    props.onSelect?.(row)
                }}
                onMultipleSelect={(rows) => {
                    setSelectedItems(rows)
                    props?.onMultipleSelect?.(rows)
                }}
                multipleSelect={props.multipleSelect}
                plugin={props.plugin}
                onDoubleClick={(row) => props.onDoubleClick?.(row)}
                onSort={(i) => dispatchGridFilter({ type: "sort", payload: i.propertyName })}
                onFilterDelete={(i) => dispatchGridFilter({ type: "deleteColumnFilter", payload: i.propertyName })}
                onPageNumberChange={(n) => dispatchGridFilter({ type: "changePageNumber", payload: { pageNumber: n } })}
                onNumberPerPageChange={(n) => dispatchGridFilter({ type: "changeNumberPerPage", payload: { numberPerPage: n } })}
            />
        </PluginWrapper>
    )
}